import { Box, Collapse, Link, type SxProps, type Theme } from '@mui/material';
import { PinDigitInput } from './PinDigitInput';

const ACCENT = '#2C3E2D';

export type PinAuthorizationFieldProps = {
  value: string;
  onChange: (pin: string) => void;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  toggleLabel?: string;
  pinLabel?: string;
  pinLength?: number;
  collapsible?: boolean;
  autoFocus?: boolean;
  sx?: SxProps<Theme>;
};

/**
 * Collapsible PIN authorization block for approval flows (refunds, cancellations, etc.).
 * Pair with `usePinVerification` or call `authService.verifyPin` on confirm.
 */
export function PinAuthorizationField({
  value,
  onChange,
  expanded,
  onExpandedChange,
  disabled = false,
  error = false,
  helperText,
  toggleLabel = 'Authorize with PIN',
  pinLabel = 'Authorization PIN',
  pinLength = 10,
  collapsible = true,
  autoFocus = false,
  sx,
}: PinAuthorizationFieldProps) {
  const pinInput = (
    <PinDigitInput
      value={value}
      onChange={onChange}
      disabled={disabled}
      label={pinLabel}
      length={pinLength}
      error={error}
      helperText={helperText}
      autoFocus={autoFocus}
    />
  );

  if (!collapsible) {
    return <Box sx={sx}>{pinInput}</Box>;
  }

  return (
    <Box sx={sx}>
      <Link
        component="button"
        type="button"
        onClick={() => onExpandedChange(!expanded)}
        sx={{
          fontSize: '0.75rem',
          color: ACCENT,
          mb: 1,
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          p: 0,
        }}
      >
        {expanded ? '▲' : '▼'} {toggleLabel}
      </Link>
      <Collapse in={expanded}>{pinInput}</Collapse>
    </Box>
  );
}
